import { GridItem } from '../../components/common/grid/GridItem'
import { Title } from '../../components/common/texts/Title'
import { Controller, UseFormReturn } from 'react-hook-form'
import { WorkPlaceLimit } from './WorkPlaceLimit'
import { ControlledDropDownMulti } from '../../components/common/inputs/Dropdown'
import { Components, OrganisationWithUserAccountsDTO, SubscriptionLevel } from '../../api/schemas/schema'
import { ControlledModulesDropDown } from './ControlledModulesDropDown'
import { makeStyles, tokens } from '@fluentui/react-components'
import { PartnerCheckbox } from './PartnerCheckbox'
import { useContext, useEffect, useMemo, useState } from 'react'
import { ModalWarningConfirm } from './ModalUncheckWarning'
import { Grid } from '../../components/common/grid/Grid'
import { DataContext } from '../../App'
import { ControlledCheckbox } from '../../components/common/inputs/ControlledCheckbox'
import { t } from '../../i18n/i18n'
import { ILanguage } from '../../interfaces/ILanguage'
const useStyles = makeStyles({
   alignEnd: {
      display: 'flex',
      alignItems: 'flex-end',
   },
   gridWrapper: {
      '@media (max-width: 1500px)': {
         display: 'flex',
         flexDirection: 'column',
      },
   },
   partnerWrap: {
      '@media (max-width: 1500px)': {
         gridColumn: 'span 12',
      },
   },
   partnerFlexWrap: {
      display: 'flex',
      gap: tokens.spacingHorizontalL,
      alignItems: 'flex-end',
      flexWrap: 'wrap',

      '@media (max-width: 1500px)': {
         flexDirection: 'row',
      },
   },
})
type OrganisationModulesFormProps = {
   organisationForm: UseFormReturn<OrganisationWithUserAccountsDTO, any, undefined>
}
export const OrganisationModulesForm = ({ organisationForm }: OrganisationModulesFormProps) => {
   const classes = useStyles()
   const { control, watch } = organisationForm
   const [showUncheckWarning, setShowUncheckWarning] = useState(false)
   const [resolveUncheck, setResolveUncheck] = useState<(result: boolean) => void>()
   const dataContext = useContext(DataContext)
   const { processes } = dataContext.state

   const processOptions = useMemo(
      () =>
         processes
            .sort((a, b) => a.text.localeCompare(b.text)) // Alphabetic order
            .map((p) => ({
               key: p.id ?? '',
               text: p.text ?? '',
               disabled: false,
            })),
      [processes]
   )

   const subscriptionOptions = useMemo(
      () =>
         Object.entries(SubscriptionLevel)
            .filter(([key, value]) => isNaN(Number(key)) && value !== SubscriptionLevel.ADD_ON) // Filter out numeric keys and 'None' as well as add on subscription which should not be selectable (desicion 5/3-2026)
            .map(([key, value]) => ({
               key: value as number,
               text: t(key as keyof ILanguage),
               disabled: false,
            })),
      []
   )

   const uncheckWarning = () => {
      return new Promise<boolean>((resolve) => {
         setShowUncheckWarning(true)
         setResolveUncheck(() => resolve)
      })
   }
   const confirmUncheck = async (confirmed: boolean) => {
      setShowUncheckWarning(false)
      resolveUncheck?.(confirmed)
   }

   const organisationModules = watch('organisation.modules') || []
   const isDemandComplianceSelected = organisationModules.some((o) => o.component === Components.DEMAND_COMPLIANCE)

   useEffect(() => {
      if (!isDemandComplianceSelected) {
         // If Demand Compliance is unchecked, clear the selected processes
         organisationForm.setValue('organisation.organisationProcessIds', [], { shouldDirty: true })
      }
   }, [isDemandComplianceSelected, organisationForm])

   return (
      <Grid className={classes.gridWrapper}>
         <GridItem size={'12/12'}>
            <Title as="h5">{t('BaseModules')}</Title>
         </GridItem>

         <GridItem size="6/12">
            <ControlledDropDownMulti name={'organisation.subscriptions'} control={control} label={t('DemandLevel')} options={subscriptionOptions} />
         </GridItem>
         <GridItem size="6/12" className={classes.partnerWrap}>
            <div className={classes.partnerFlexWrap}>
               <Controller
                  name="organisation.workplaceLimit"
                  control={control}
                  defaultValue={0}
                  render={({ field: { value, onChange } }) => <WorkPlaceLimit value={value} onChange={onChange} min={0} max={500} />}
               />
               <PartnerCheckbox name="organisation.isPartner" control={control} label={t('Partner')} uncheckWarning={uncheckWarning} />
            </div>
         </GridItem>

         {/* Modules */}
         <GridItem size="12/12">
            <ControlledModulesDropDown name={'organisation.modules'} control={control} label={t('Modules')} watch={watch} disabled={false} />
         </GridItem>

         {/* Processes */}
         {isDemandComplianceSelected && (
            <GridItem size="12/12">
               <ControlledDropDownMulti name={'organisation.organisationProcessIds'} control={control} label={t('Processes')} options={processOptions} />
            </GridItem>
         )}
         {/* 
         <GridItem size="12/12">
            <ControlledCheckbox name={'organisation.strategicDemandAssessment'} control={control} label={t('StrategicDemandAssessment')} />
         </GridItem> */}

         <ModalWarningConfirm
            open={showUncheckWarning}
            title={t('ConfirmUncheck')}
            message={t('ConfirmUncheckMsg')}
            confirmButtonText={t('DeleteSandboxes')}
            confirmFunction={confirmUncheck}
         />
      </Grid>
   )
}
